/**
 * Small DOM helpers shared by the App Shell widgets.
 *
 * Deliberately tiny: the shell is a toolbar, a name box, a formula bar, a canvas, a
 * status bar, and a tab strip. A framework would cost more in bundle than it saves
 * in code here, and the grid hot path must stay out of any reconciliation loop
 * (design: "no React reconciliation in the grid hot path").
 */

/** Creates an element, applies attributes, and appends children. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  init: {
    className?: string;
    text?: string;
    testid?: string;
    title?: string;
    role?: string;
    ariaLabel?: string;
    style?: Partial<CSSStyleDeclaration>;
    attrs?: Record<string, string>;
  } = {},
  children: readonly Node[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (init.className !== undefined) node.className = init.className;
  if (init.text !== undefined) node.textContent = init.text;
  if (init.testid !== undefined) node.dataset['testid'] = init.testid;
  if (init.title !== undefined) node.title = init.title;
  if (init.role !== undefined) node.setAttribute('role', init.role);
  if (init.ariaLabel !== undefined) node.setAttribute('aria-label', init.ariaLabel);
  if (init.style !== undefined) Object.assign(node.style, init.style);
  for (const [name, value] of Object.entries(init.attrs ?? {})) node.setAttribute(name, value);
  for (const child of children) node.appendChild(child);
  return node;
}

/** A `<button>` with a click handler and an accessible label. */
export function button(label: string, onClick: () => void, testid?: string): HTMLButtonElement {
  const node = el('button', {
    text: label,
    ...(testid === undefined ? {} : { testid }),
    attrs: { type: 'button' },
  });
  node.addEventListener('click', onClick);
  return node;
}

/** Installs the shell's stylesheet once. */
export function installStyles(css: string, id: string): void {
  if (document.getElementById(id) !== null) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}
